import React from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Truck, LayoutDashboard, FileText, Package, Receipt, MessageSquare, Upload, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { APEX_LOGO } from "@/lib/brand";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "", label: "Overview", icon: LayoutDashboard },
  { to: "quotes", label: "Quotes", icon: FileText },
  { to: "shipments", label: "Shipments", icon: Package },
  { to: "invoices", label: "Invoices", icon: Receipt },
  { to: "documents", label: "Documents", icon: Upload },
  { to: "messages", label: "Messages", icon: MessageSquare },
];

const CARRIER_NAV = [
  { to: "", label: "Overview", icon: LayoutDashboard },
  { to: "loads", label: "Loads", icon: Package },
  { to: "documents", label: "Documents", icon: Upload },
  { to: "invoices", label: "Payments", icon: Receipt },
  { to: "banking", label: "Banking", icon: FileText },
];

export default function PortalShell({ children, kind }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const items = kind === "carrier" ? CARRIER_NAV : NAV;
  const base = kind === "carrier" ? "/portal/carrier" : "/portal/customer";

  const Sidebar = () => (
    <aside className="w-64 bg-[#0B3D6E] text-slate-300 min-h-screen flex flex-col">
      <Link to="/" className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
        <div className="bg-white rounded-md p-1.5"><img src={APEX_LOGO} alt="Apex" className="h-10 w-auto object-contain" /></div>
        <div className="text-[10px] font-label text-slate-400">{kind === "carrier" ? "Carrier" : "Customer"} Portal</div>
      </Link>
      <nav className="flex-1 py-6 px-3 space-y-1">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to} to={to === "" ? base : `${base}/${to}`} end={to === ""}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-[#2E7BC7] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`
            }
            data-testid={`portal-nav-${label.toLowerCase()}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-sm text-white font-semibold">{user?.name}</div>
        <div className="text-xs text-slate-400 mb-3">{user?.email}</div>
        <button onClick={async () => { await logout(); nav("/"); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="portal-logout-btn">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden md:block"><Sidebar /></div>
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger data-testid="portal-mobile-menu"><Menu className="h-5 w-5 text-[#0B3D6E]" /></SheetTrigger>
                <SheetContent side="left" className="p-0 w-64"><Sidebar /></SheetContent>
              </Sheet>
            </div>
            <h1 className="font-display font-bold text-lg text-[#0B3D6E]">
              {items.find((i) => (i.to === "" ? loc.pathname === base : loc.pathname.endsWith(i.to)))?.label || "Dashboard"}
            </h1>
          </div>
          <Link to="/quote" className="btn-primary-apex text-sm" data-testid="portal-request-quote-btn">Request Quote</Link>
        </header>
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
